import React, { useCallback } from 'react';
import { convertMultipleSeriesDataToExcel } from 'venn-utils';
import { WATERMARK_POSITION_TOP } from '../../../studio-blocks';

import useExportUpdate from '../../../studio-blocks/logic/useExportUpdate';
import StyledEmptyState from '../../../studio-blocks/components/StyledEmptyState';
import DownloadableContentBlock, { EmptyHeaderSpace } from '../../../content-block/DownloadableContentBlock';
import { useFilteredRequestsAndAnalyses } from '../../utils/hooks/use-filtered-requests';
import { useRecoilValue } from 'recoil';
import { blockExportMetadata, blockSubjectHasProxyState, customizedBlock, isReportState } from 'venn-state';
import { useSelectedMetrics } from '../../../studio-blocks/logic/useSelectedMetrics';
import type { AnalysisProps } from '../../types';
import { isEmpty } from 'lodash';
import LineChart from '../../../studio-blocks/components/charts/LineChart';
import { useBlockContext } from '../../contexts/block-context';
import { useAppPrintMode } from '../../../print';
import { useRollingCorrelation } from '../../utils/hooks/use-rolling-correlation';

export const RollingCorrelationContent = ({ id }: { id: string }) => {
  const exportMetaData = useRecoilValue(blockExportMetadata(id));
  const { requests, analyses } = useFilteredRequestsAndAnalyses(id);
  const isReport = useRecoilValue(isReportState);
  const { inPrintModeOrReportLab } = useAppPrintMode();

  return (
    <Chart
      id={id}
      requests={requests}
      analyses={analyses}
      exportMetaData={exportMetaData}
      inPrintMode={inPrintModeOrReportLab}
      isReport={isReport}
    />
  );
};

const Chart = ({ id, requests, analyses, exportMetaData, inPrintMode }: AnalysisProps) => {
  const metrics = useSelectedMetrics();
  const metric = metrics ? metrics[0] : undefined;

  const subjectHasProxy = useRecoilValue(blockSubjectHasProxyState(id));
  const { relativeToBenchmark, infoGraphicType, header } = useRecoilValue(customizedBlock(id));
  const { blockRef: downloadableContentRef } = useBlockContext();

  const [series, exportableSeries] = useRollingCorrelation(analyses, metric, requests, subjectHasProxy);

  const defaultHeader =
    relativeToBenchmark && metric?.relativeLabel ? metric.relativeLabel : !metric ? 'Builder Chart' : metric.label;

  const excelDataFn = useCallback(
    () =>
      convertMultipleSeriesDataToExcel(
        exportableSeries.map((s) => ({ name: s.name, series: s.data })),
        metric?.dataType !== 'NUMERIC',
      ),
    [exportableSeries, metric?.dataType],
  );

  useExportUpdate({ exportMetaData, excelDataFn, selectedRefId: id });

  if (isEmpty(series)) {
    return <StyledEmptyState selectedRefId={id} header="Unable to run analysis" message="Chart is empty" />;
  }

  const Chart =
    infoGraphicType === 'LINE' ? (
      <LineChart
        start={requests?.[0]?.dateRange.from}
        end={requests?.[0]?.dateRange.to}
        series={series}
        frequency={requests?.[0]?.frequency ?? 'MONTHLY'}
        yAxisUnitFormat={metric?.dataType === 'NUMERIC' ? 'ratio' : 'percent'}
        inPrintMode={inPrintMode}
      />
    ) : null;

  if (inPrintMode) {
    return Chart;
  }

  return (
    <DownloadableContentBlock
      header={<EmptyHeaderSpace />}
      noBorder
      downloadable={{
        png: true,
        options: {
          fileName: header ?? defaultHeader,
          watermark: { top: WATERMARK_POSITION_TOP, right: 20 },
        },
        tracking: {
          description: 'BUILDER',
          relativeToBenchmark,
          userUploaded: false,
          subjectType: undefined,
          subjectId: undefined,
        },
        disabled: series.length === 0,
        target: downloadableContentRef,
      }}
    >
      {Chart}
    </DownloadableContentBlock>
  );
};
